import React, { useEffect, useState } from 'react';
import { Loader2, CalendarClock } from 'lucide-react';
import { contactLabel } from '../PrestationPlanning/prestationPlanningShared';

const api = (path: string) => {
    const token = localStorage.getItem('token');
    return fetch(`/api${path}`, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }).then((r) => r.json());
};

interface Employee { id: string; first_name: string; last_name: string; }
interface Appointment {
    id: string;
    crm_first_name?: string;
    crm_last_name?: string;
    crm_company_name?: string;
    employee_ids: string[];
    prestation_label: string;
    start_time: string;
    appointment_date: string;
}

/** Liste des salariés assignés à un rendez-vous, tel que déjà utilisé dans "Mon Planning" — permet de
 *  voir immédiatement qui d'autre intervient sur un rendez-vous partagé, sans avoir à ouvrir son détail. */
const employeeNamesLabel = (appt: Appointment, employees: Employee[]) =>
    (appt.employee_ids || [])
        .map((id) => {
            const e = employees.find((x) => x.id === id);
            return e ? `${e.first_name} ${e.last_name}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');

const LiveClock: React.FC = () => {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);
    const hh = String(time.getHours()).padStart(2, '0');
    const mm = String(time.getMinutes()).padStart(2, '0');
    const ss = String(time.getSeconds()).padStart(2, '0');
    return (
        <div className="flex flex-col items-center justify-center py-8">
            <div className="text-content-muted text-[10px] font-bold uppercase tracking-widest mb-1">Heure actuelle</div>
            <div className="flex items-center gap-2 text-4xl md:text-5xl font-black tracking-tighter text-content font-mono">
                <span>{hh}</span>
                <span className="animate-pulse text-brand-500">:</span>
                <span>{mm}</span>
                <span className="animate-pulse text-brand-500 text-2xl md:text-3xl">:</span>
                <span className="text-2xl md:text-3xl text-content-muted w-[1.2ch]">{ss}</span>
            </div>
            <div className="text-content-muted text-sm mt-1 font-medium capitalize">
                {time.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
        </div>
    );
};

/** Onglet "Dashboard" dédié dans la barre latérale du salarié Prestation (distinct de "Mon Planning") —
 *  date/heure en direct + liste des prochains rendez-vous, avec la liste complète des salariés assignés
 *  quand un rendez-vous en compte plusieurs. */
export const PrestationEmployeeDashboard: React.FC = () => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [upcoming, setUpcoming] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [emps, rows] = await Promise.all([
                    api('/employes'),
                    api('/employe/prestation-planning/upcoming?limit=10'),
                ]);
                setEmployees((Array.isArray(emps) ? emps : []).filter((e: any) => !e.deleted_at));
                setUpcoming(Array.isArray(rows) ? rows : []);
            } catch (e) {
                console.error('Failed to load employee dashboard', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du tableau de bord...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-2xl font-black text-content">Tableau de bord</h1>
                <p className="text-sm text-content-muted mt-1">Vos prochains rendez-vous.</p>
            </header>

            <div className="rounded-2xl border border-border bg-surface">
                <LiveClock />
            </div>

            <div className="rounded-2xl border border-border bg-surface p-5 space-y-3">
                <h2 className="text-xs font-black uppercase tracking-wider text-content-muted flex items-center gap-1.5">
                    <CalendarClock size={14} /> Prochains rendez-vous
                </h2>
                {upcoming.length === 0 ? (
                    <p className="text-sm text-content-muted italic py-6 text-center">Aucun rendez-vous à venir.</p>
                ) : (
                    <div className="space-y-2">
                        {upcoming.map((appt) => {
                            const others = employeeNamesLabel(appt, employees);
                            return (
                                <div key={appt.id} className="p-3 rounded-xl bg-surface-soft border border-border">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="shrink-0 w-11 h-11 rounded-lg bg-brand-500/10 text-brand-500 flex flex-col items-center justify-center leading-none">
                                                <span className="text-[9px] font-bold uppercase">{new Date(appt.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'short' })}</span>
                                                <span className="text-sm font-black">{new Date(appt.appointment_date + 'T00:00:00').getDate()}</span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-content truncate">{contactLabel(appt)}</p>
                                                <p className="text-xs text-content-muted truncate">{appt.prestation_label}</p>
                                            </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <p className="text-sm font-bold text-content">{appt.start_time}</p>
                                            <p className="text-[10px] text-content-muted capitalize">{new Date(appt.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short' })}</p>
                                        </div>
                                    </div>
                                    {others && (
                                        <p className="text-[11px] text-content-muted mt-2 pt-2 border-t border-border">
                                            Salarié(s) assigné(s) : <span className="text-content font-semibold">{others}</span>
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
